import * as React from 'react';
import CanvasDraw from '@win11react/react-canvas-draw';

import moment from 'moment';

import { useNavigate } from 'react-router-dom';

import { WithDeleted } from '@common/typescript/objects/WithDeleted';
import Button from '@common/react/components/Forms/Button';
import Loader from '@common/react/components/Core/LoaderProvider/Loader';
import { ItemProvider } from '@common/react/components/Core/ItemProvider/ItemProvider';
import { showDate } from '@common/react/utils/timeUtils';
import { useModal } from '@common/react/components/Modal/ModalContextProvider';
import { useApplicationContext } from '@common/react/components/Core/Application/Application';

import { dataURLtoFile, getDataUrl } from '@commonTuna/react/utils/Utils';

import '@commonTuna/react/scss/components/consentFormWithSignature.scss';
import '@commonTuna/react/scss/components/consentFormSwitcher.scss';

import { useCompanySettingsContext } from '@app/components/UI/CompanySettingsSetter/CompanySettingsSetter';
import { User, UserRole } from '@app/objects/User';

import '@app/scss/components/disclosureTemplate.scss';

interface BaseComponentProps {
	userId: number;
	onAccept(): void;
}

const lazyRadius = 0.5;

type ComponentProps = BaseComponentProps;

interface ResearchUserHtml extends WithDeleted {
	html: string;
}

const UserResearchSignatureForm: React.FC<ComponentProps> = ({
	userId,
	onAccept,
}) => {
	const { openErrorMessage } = useModal();

	const date = React.useMemo(() => {
		return showDate(+new Date(), false, false);
	}, []);
	const canvasHeight = 150;
	const canvas = React.useRef<CanvasDraw | null>(null);
	const parent = React.useRef<HTMLDivElement | null>(null);
	const [isLoading, setIsLoading] = React.useState<boolean>(false);
	const [message, setMessage] = React.useState<string>('');
	const [width, setWidth] = React.useState<number>();

	const accept = () => {
		const signatureDataUrl = getDataUrl(canvas.current);
		setMessage('');

		if (signatureDataUrl) {
			setIsLoading(true);

			const setImg = (dataUrl: string | null, initialField: string) => {
				return dataUrl ? dataURLtoFile(dataUrl, `${initialField}_${Date.now()}.png`) : null;
			};

			const signatureImg = setImg(signatureDataUrl, 'signature');

			const formData = new FormData();

			signatureImg && formData.append('signature', signatureImg);
			userId && formData.append('userId', userId.toString());

			fetch('getResearchUser', {
				credentials: 'same-origin',
				method: 'POST',
				body: formData,
			})
				.then((result) => {
					onAccept();
				})
				.catch(openErrorMessage)
				.finally(() => setIsLoading(false));
		} else {
			setMessage('Signature is empty');
		}
	};

	const clearCanvas = (canvas) => {
		(canvas.current as CanvasDraw)?.clear();
	};

	React.useEffect(() => {
		setWidth(calcCanvasWidth(parent));
	}, []);

	const calcCanvasWidth = (parent: React.MutableRefObject<HTMLDivElement | null>) => {
		return (parent.current && parseInt(window.getComputedStyle(parent.current).width, 10)) || undefined;
	};

	return <ItemProvider<ResearchUserHtml>
		id={userId}
		loadRequest="getResearchUserHtml"
		saveRequest="researchUser"
		onLoad={(res) => {
			canvas.current && clearCanvas(canvas);
		}}
		onLoadRequestError={(e) => {
			setMessage(e);
		}}
		showLoader={false}
	>
		{({ state: { item, itemLoading: templateLoading } }) => {
			const template = item?.html || '';

			return <div className="consent-modal__container">
				{templateLoading
					? <Loader />
					: (
						<div className="consent-modal__viewer ResearchUser-template consent-template mb10">
							<div className="consent-template__inner" dangerouslySetInnerHTML={{ __html: template }} />
						</div>
					)
				}
				<div className="consent-form-with-signature__bottom-form">
					<div className="consent-form-with-signature__bottom-form__inner">
						<table className="mb10">
							<tbody>
								<tr>
									<td style={{ width: '80%' }} className="sign-bg sign-border consent-form-with-signature__signature">
										<div className="sign-border consent-form-with-signature__canvas-parent" ref={parent}>
											{width && <CanvasDraw
												brushRadius={2}
												ref={canvas}
												canvasWidth={width}
												gridColor="rgba(150,150,150,0)"
												canvasHeight={canvasHeight}
												hideInterface
												className="canvas-wrapper"
												lazyRadius={lazyRadius}
											/>}
										</div>
									</td>
									<td style={{ width: '5%' }} />
									<td style={{ width: '15%' }} className="consent-form-with-signature__date">{date}</td>
								</tr>
								<tr>
									<td className="consent-signature-flex">
										Signature
										<Button
											className="btn btn-sm btn-light"
											onClick={() => clearCanvas(canvas)}
										>
											Clear
										</Button>
									</td>
									<td style={{ width: '5%' }} />
									<td
										style={{ width: '15%' }}
										className="consent-form-with-signature__date consent-form-with-signature__date-mobile"
									>
										{date}
									</td>
									<td style={{ width: '25%' }}>Date</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
				<div className="consent-signature-buttons form-group">
					{!!template
						&& <Button
							disabled={templateLoading || isLoading}
							isLoading={isLoading}
							className="btn btn-success mr10"
							onClick={() => accept()}
						>
							Sign
						</Button>
					}
				</div>
				{message && <div className="alert alert-danger">{message}</div>}
			</div>;
		}}
	</ItemProvider>;
};

const UserResearchSignatureFormModal: React.FC = ({ children }) => {
	const { renderModal, openSuccessMessage } = useModal();

	const { getUser, updateUser } = useApplicationContext();
	const { companySettings } = useCompanySettingsContext();
	const navigate = useNavigate();

	const user = getUser<User>();

	const [visible, setVisible] = React.useState<boolean>(false);

	if (user.role !== UserRole.External || companySettings.researchTemplateId === null) return null;

	React.useEffect(() => {
		if (!user.researchSignedDate
				|| user.researchSignedDate < moment().utc().add(-companySettings.researchTemplateValidPeriod, 'days').valueOf()) {
			setVisible(true);
		}
	}, [user, visible]);

	return <>
		{renderModal(visible, () => setVisible(false), {
			title: 'Research Signature',
			footer: null,
			width: 'min(90%, 920px)',
			destroyOnHidden: true,
			closable: false,
			children: <UserResearchSignatureForm
				onAccept={() => {
					openSuccessMessage?.('Document successfully created');
					setVisible(false);
					updateUser({ researchSignedDate: moment().utc().valueOf() });
					navigate({ pathname: '/document-list' });
				}}
				userId={user?.id}
			/>,
		})}
	</>;
};

export default UserResearchSignatureFormModal;
